import React from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {Trans, useTranslation} from 'react-i18next';
import {useParams} from 'react-router';

import {skipToken} from '@reduxjs/toolkit/query';

import {
  Modal,
  ModalActions,
  ModalCancelButton,
  ModalContent,
  ModalHeader,
  ModalSubmitButton,
  SectionWithBorderField,
  showModal,
} from '@edna/components';
import {FieldError} from '@edna/components/FormField';
import {Stack} from '@edna/components/primitives';
import {useOnChange} from '@edna/hooks';
import {ECurrency, TCurrency} from '@edna/utils';

import {Hint, SecondaryText, Text} from 'src/components/primitives';
import {Banner} from 'src/containers/Banner';
import {useGetSupportClientQuery} from 'src/containers/SupportClients/api';
import {useGetTenantDataQuery} from 'src/containers/User';
import {joiResolver} from 'src/utils/validator';

import {
  useConnectStripeAutopaymentMutation,
  useGetStripeAutopaymentInfoQuery,
  useGetStripeAutopaymentRestrictionsQuery,
  useUpdateStripeAutopaymentSettingsMutation,
} from '../../api';
import {MAX_AMOUNT, TAutopaymentFormData} from '../../definitions';
import * as S from '../../style';
import {useAutopaymentRecommendedThreshold} from '../useAutopaymentRecommendedThreshold';

type TValidatorProps = {minThreshold?: number; minAmount?: number; currency: TCurrency};

const makeValidator = ({currency, minThreshold = 0, minAmount = 0}: TValidatorProps) =>
  joiResolver((joi) =>
    joi.object({
      threshold: joi
        .number()
        .required()
        .max(MAX_AMOUNT)
        .custom((value, helpers) => {
          if (Number(value) < minThreshold) {
            return helpers.error('minThreshold', {currency, limit: minThreshold});
          }

          return value;
        }),
      amount: joi
        .number()
        .required()
        .max(MAX_AMOUNT)
        .custom((value, helpers) => {
          if (Number(value) < minAmount) {
            return helpers.error('minAmount', {currency, limit: minAmount});
          }

          return value;
        }),
    }),
  );

type TModalProps = {
  isConnect?: boolean;
  closeModal: TEmptyFunction;
};

const StripeSettingsModal = React.memo<TModalProps>(({closeModal, isConnect}) => {
  const {t} = useTranslation();
  const {id: supportTenantId} = useParams<'id'>();

  const {data: supportClient} = useGetSupportClientQuery(supportTenantId ?? skipToken);
  const {data: info} = useGetStripeAutopaymentInfoQuery();
  const {data: restrictions} = useGetStripeAutopaymentRestrictionsQuery();
  const {data: tenantData} = useGetTenantDataQuery();
  const [connectAutopayment, {isLoading: isConnecting}] = useConnectStripeAutopaymentMutation();
  const [updateAutopaymentSettings, {isLoading: isUpdating}] =
    useUpdateStripeAutopaymentSettingsMutation();

  const recommendedThreshold = useAutopaymentRecommendedThreshold();

  const currency =
    (supportTenantId ? supportClient?.defaultCurrency : tenantData?.defaultCurrency) ??
    ECurrency.USD;

  const defaultValues = React.useMemo(
    () => ({
      threshold:
        info?.threshold ?? Math.max(recommendedThreshold, restrictions?.minimumThreshold ?? 0),
      amount: info?.amount ?? restrictions?.minimumAmount,
    }),
    [restrictions, info, recommendedThreshold],
  );

  const resolver = React.useMemo(
    () =>
      makeValidator({
        minAmount: restrictions?.minimumAmount,
        minThreshold: restrictions?.minimumThreshold,
        currency,
      }),
    [restrictions, currency],
  );

  const methods = useForm<TAutopaymentFormData>({
    mode: 'all',
    defaultValues,
    resolver,
  });

  useOnChange(() => methods.reset(defaultValues), [defaultValues]);

  const amountError = methods.formState.errors.amount;
  const thresholdError = methods.formState.errors.threshold;

  const handleSave = React.useCallback(async () => {
    const values = methods.getValues();

    try {
      if (isConnect) {
        const result = await connectAutopayment(values).unwrap();

        if (result?.stripeUrl) {
          window.open(result.stripeUrl, '_blank', 'noreferrer');
        }
      } else {
        await updateAutopaymentSettings(values).unwrap();
      }

      closeModal();
    } catch (e) {}
  }, [closeModal, methods.getValues, isConnect]);

  const threshold = methods.watch('threshold');

  const isShowWarning = React.useMemo(() => {
    if (thresholdError || !threshold) {
      return false;
    }

    return Number(threshold) < recommendedThreshold;
  }, [threshold, recommendedThreshold, thresholdError]);

  return (
    <Modal width="582px">
      <FormProvider {...methods}>
        <ModalHeader>
          {t(
            `BalanceReplenishment:stripeAutopaymentSettingsModal.${isConnect ? 'titleConnect' : 'titleEdit'}`,
          )}
        </ModalHeader>
        <ModalContent>
          <S.Wrapper>
            {!!recommendedThreshold && (
              <SecondaryText size="S">
                <Trans
                  i18nKey="BalanceReplenishment:stripeAutopaymentSettingsModal.licensesInfo"
                  values={{threshold: recommendedThreshold, currency}}
                >
                  <Text size="S" weight={500} isInline />
                </Trans>
              </SecondaryText>
            )}
            <SectionWithBorderField
              name="threshold"
              size="2"
              label={t('BalanceReplenishment:stripeAutopaymentSettingsModal.threshold')}
              htmlFor="threshold"
            >
              <Stack size="2" direction="ROW" align="center">
                <S.NumberInputField name="threshold" showError={false} noMargin />
                <Text size="S">{currency}</Text>
              </Stack>
              {!!thresholdError && (
                <FieldError>
                  {t(thresholdError?.message ?? '', thresholdError?.ref?.value) as string}
                </FieldError>
              )}
              <Hint>
                {t('BalanceReplenishment:stripeAutopaymentSettingsModal.minThreshold', {
                  currency,
                  threshold: restrictions?.minimumThreshold,
                })}
              </Hint>
            </SectionWithBorderField>
            <SectionWithBorderField
              name="amount"
              size="2"
              label={t('BalanceReplenishment:stripeAutopaymentSettingsModal.amount')}
              htmlFor="amount"
            >
              <Stack size="2" direction="ROW" align="center">
                <S.NumberInputField name="amount" showError={false} noMargin />
                <Text size="S">{currency}</Text>
              </Stack>
              {!!amountError && (
                <FieldError>
                  {t(amountError?.message ?? '', amountError?.ref?.value) as string}
                </FieldError>
              )}
              <Hint>
                {t('BalanceReplenishment:stripeAutopaymentSettingsModal.minAmount', {
                  currency,
                  amount: restrictions?.minimumAmount,
                })}
              </Hint>
            </SectionWithBorderField>
          </S.Wrapper>
          {isShowWarning && (
            <Banner type="WARNING" fontSize="S">
              {t('BalanceReplenishment:stripeAutopaymentSettingsModal.incorrectSettingsWarning')}
            </Banner>
          )}
          <SecondaryText size="S">
            {t('BalanceReplenishment:stripeAutopaymentSettingsModal.acceptanceInfo')}
          </SecondaryText>
        </ModalContent>
        <ModalActions>
          <ModalCancelButton onClick={closeModal}>{t('Common:button.cancel2')}</ModalCancelButton>
          <ModalSubmitButton
            onClick={methods.handleSubmit(handleSave)}
            disabled={
              !methods.formState.isDirty || !methods.formState.isValid || isConnecting || isUpdating
            }
          >
            {t('Common:button.apply')}
          </ModalSubmitButton>
        </ModalActions>
      </FormProvider>
    </Modal>
  );
});

StripeSettingsModal.displayName = 'StripeSettingsModal';

const showStripeSettingsModal = (isConnect?: boolean) =>
  showModal((closeModal) => <StripeSettingsModal isConnect={isConnect} closeModal={closeModal} />);

export {showStripeSettingsModal};
